// Lazy loader for Leaflet (free, open-source, OpenStreetMap-tile-backed).
//
// Why dynamic CDN instead of an npm dep?
//   1. Leaflet is only used by the delivery tracking map. Most customer
//      sessions don't have an active delivery, so eagerly bundling ~40 KB
//      of map code into every customer-portal page is wasteful.
//   2. CDN tiles + script live on unpkg, which is mirrored by Cloudflare.
//      No API key, no quota, no external SaaS dependency.
//   3. Keeps the customer-portal Vite build slim (no leaflet workspace
//      package required).
//
// `ensureLeaflet()` is idempotent — the script and CSS are injected once
// and cached on the window object. Subsequent calls resolve to the same
// global namespace.
//
// We don't tree-shake — Leaflet is a UMD library that attaches to
// `window.L`. We surface a thin type wrapper for the bits we actually use
// (map, marker, tile layer) so the rest of the code stays typed.

/** Subset of the Leaflet types we use. Full types live at `@types/leaflet`. */
export interface LeafletLatLngBounds {
  extend(point: [number, number]): LeafletLatLngBounds;
  pad(amount: number): LeafletLatLngBounds;
}

export interface LeafletMarker {
  setLatLng(point: [number, number]): LeafletMarker;
  getLatLng(): { lat: number; lng: number };
  addTo(map: LeafletMap): LeafletMarker;
  remove(): void;
  bindPopup(html: string): LeafletMarker;
  setIcon(icon: unknown): LeafletMarker;
}

export interface LeafletTileLayer {
  addTo(map: LeafletMap): LeafletTileLayer;
}

export interface LeafletMap {
  setView(point: [number, number], zoom: number): LeafletMap;
  fitBounds(bounds: LeafletLatLngBounds, opts?: { padding?: [number, number] }): LeafletMap;
  remove(): void;
  invalidateSize(): LeafletMap;
}

export interface LeafletNS {
  map(el: HTMLElement, opts?: Record<string, unknown>): LeafletMap;
  marker(
    point: [number, number],
    opts?: { icon?: unknown; title?: string },
  ): LeafletMarker;
  tileLayer(urlTemplate: string, opts?: Record<string, unknown>): LeafletTileLayer;
  latLngBounds(points: Array<[number, number]>): LeafletLatLngBounds;
  divIcon(opts: {
    html: string;
    className?: string;
    iconSize?: [number, number];
    iconAnchor?: [number, number];
  }): unknown;
}

const LEAFLET_VERSION = "1.9.4";
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;

let cached: Promise<LeafletNS> | null = null;

declare global {
  interface Window {
    L?: LeafletNS;
  }
}

export function ensureLeaflet(): Promise<LeafletNS> {
  if (cached) return cached;

  cached = new Promise<LeafletNS>((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    // Inject CSS once.
    if (!document.querySelector(`link[data-leaflet]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      link.setAttribute("data-leaflet", "true");
      // Cross-origin isn't required for stylesheets but mirrors the script.
      document.head.appendChild(link);
    }

    // Inject JS — if a previous attempt added it, just wait for `window.L`.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-leaflet]`,
    );
    if (existing) {
      existing.addEventListener("load", () => {
        if (window.L) resolve(window.L);
        else reject(new Error("Leaflet loaded but window.L is undefined"));
      });
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Leaflet")),
      );
      return;
    }

    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.setAttribute("data-leaflet", "true");
    script.addEventListener("load", () => {
      if (window.L) {
        resolve(window.L);
      } else {
        reject(new Error("Leaflet loaded but window.L is undefined"));
      }
    });
    script.addEventListener("error", () =>
      reject(new Error("Failed to load Leaflet from CDN")),
    );
    document.head.appendChild(script);
  });

  // If loading fails, clear the cache so a subsequent retry can try again.
  cached.catch(() => {
    cached = null;
  });

  return cached;
}
